// filtri/proporzioni.ts — v034: i filtri disegnano tutto in unità «s» (distanza tra gli occhi). Sono stati fatti e
// provati sul volto sintetico del banco, che ha la testa stretta: larghezza ai zigomi 1.87 s. Sui volti veri (misurati
// con MediaPipe su foto vere, banco/veri.mjs) è circa 2.3 s e la fronte→mento è 2.7–3.0 s invece di 2.5 s: cappelli,
// corone, parrucche, orecchie e caschi venivano piccoli e «appoggiati», le deformazioni si vedevano appena.
// Il motore passa quindi ai filtri una scala corretta per categoria. Non si toccano le categorie che lavorano
// vicino alla bocca (baffi, barbe, naso e bocca: più grandi finirebbero nella zona della bocca, che resta libera) né i
// filtri di solo colore. La zona della bocca e la stabilizzazione usano sempre la scala vera.
import type { CategoriaFiltro, ManifestFiltro, Volto } from "./tipi.ts";

export const FATTORE_CATEGORIA: Readonly<Record<CategoriaFiltro, number>> = {
  testa: 1.22,
  occhi: 1.08,
  "naso-bocca": 1,
  "barba-baffi": 1,
  animali: 1.2,
  deformazione: 1.2,
  colore: 1,
  mestieri: 1.2,
  reattivi: 1.12,
  scenici: 1.15,
  stagionali: 1.18,
};

/** Correzioni per singolo filtro (id → fattore), dove la categoria non basta. */
export const FATTORE_FILTRO: Readonly<Record<string, number>> = {};

export function fattoreProporzioni(m: Pick<ManifestFiltro, "id" | "categoria">): number {
  return FATTORE_FILTRO[m.id] ?? FATTORE_CATEGORIA[m.categoria] ?? 1;
}

/** Il volto come lo vede il filtro: stessi punti, scala corretta. Lo stesso oggetto se il fattore è 1. */
export function voltoPerFiltro(v: Volto | null, fattore: number): Volto | null {
  if (!v || fattore === 1) return v;
  return { ...v, posa: { ...v.posa, scala: v.posa.scala * fattore } };
}

// --- v034: ombra morbida sotto gli oggetti -------------------------------------------------------------------------
// Cappelli, occhiali, orecchie, baffi… con una piccola ombra sotto sembrano appoggiati sul volto (prima sembravano
// disegnati sopra). Solo per le categorie di oggetti: niente ombra per colori, deformazioni ed effetti di luce
// (scenici, reattivi), dove sporcherebbe i bagliori. La qualità automatica la toglie sui telefoni lenti.
const CON_OMBRA: ReadonlySet<CategoriaFiltro> = new Set(["testa", "occhi", "naso-bocca", "barba-baffi", "animali", "mestieri", "stagionali"]);

export function conOmbra(m: Pick<ManifestFiltro, "categoria">): boolean {
  return CON_OMBRA.has(m.categoria);
}

/** Disegna lo strato del filtro con l'ombra morbida (s = distanza tra gli occhi, in pixel). */
export function disegnaConOmbra(ctx: CanvasRenderingContext2D, strato: CanvasImageSource, s: number): void {
  ctx.save();
  ctx.shadowColor = "rgba(12, 6, 30, 0.38)";
  ctx.shadowBlur = Math.max(2, s * 0.07);
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = Math.max(1, s * 0.035);
  ctx.drawImage(strato, 0, 0);
  ctx.restore();
}
